import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Truck, Tag, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { formatPrice, resolveImage } from '../lib/api';
import { useUserDiscount, applyDiscount } from '../hooks/useUserDiscount';

/**
 * Vue tableau dense des produits (style AERA-Online).
 * Colonnes : Produit · Économies · Prix · Livraison · Action
 *
 * Compatible avec la même liste de produits que ProductCard — pas de fetch supplémentaire.
 */
const ProductTableView = ({ products, onAddToCart }) => {
  const { tier } = useUserDiscount();

  return (
    <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden" data-testid="product-table-view">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider border-b border-[#E8E0D0]">
            <tr>
              <th className="px-4 py-3">Produit</th>
              <th className="px-4 py-3 hidden sm:table-cell">Économies</th>
              <th className="px-4 py-3 text-right">Prix</th>
              <th className="px-4 py-3 hidden lg:table-cell">Livraison</th>
              <th className="px-4 py-3 text-right w-32"></th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => {
              const prices = p.variants.map(v => v.price);
              const minPrice = Math.min(...prices);
              const maxPrice = Math.max(...prices);
              const priceInfo = applyDiscount(minPrice, tier);

              // "Économies" : écart % entre l'offre la plus chère et la plus basse (compétition fournisseurs)
              const offerSpread = maxPrice > minPrice
                ? Math.round((1 - minPrice / maxPrice) * 100)
                : 0;

              const totalStock = p.variants.reduce((s, v) => s + (v.stock || 0), 0);
              const inStock = totalStock > 0;

              return (
                <tr key={p.id}
                  data-testid={`product-table-row-${p.id}`}
                  className="border-b border-[#F5F1EA] hover:bg-[#FAF7F2] transition-colors">
                  {/* Produit */}
                  <td className="px-4 py-3">
                    <Link to={`/produit/${p.id}`} className="flex items-center gap-3 group">
                      <div className="w-14 h-14 rounded-md bg-[#F5F1EA] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {p.image
                          ? <img src={resolveImage(p.image)} alt={p.name} className="w-full h-full object-cover" />
                          : <ImageIcon className="w-5 h-5 text-[#C9BFAE]" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[#2D2A26] group-hover:text-[#1F4D3F] line-clamp-2 leading-tight">
                          {p.name}
                        </div>
                        <div className="text-xs text-[#7A6F5C] mt-0.5">
                          {p.brand} · {p.variants.length} variante{p.variants.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </Link>
                  </td>

                  {/* Économies (compétition multi-fournisseurs) */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {offerSpread > 0 ? (
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                        <Tag className="w-3 h-3" />
                        jusqu'à −{offerSpread}%
                      </div>
                    ) : <span className="text-xs text-[#C9BFAE]">—</span>}
                  </td>

                  {/* Prix */}
                  <td className="px-4 py-3 text-right">
                    {priceInfo.hasDiscount ? (
                      <div>
                        <div className="font-serif text-base font-semibold text-[#1F4D3F]" data-testid={`table-price-${p.id}`}>
                          {formatPrice(priceInfo.discounted)}
                        </div>
                        <div className="text-xs text-[#7A6F5C] line-through">{formatPrice(priceInfo.original)}</div>
                        <div className="text-[10px] text-[#7A6520] mt-0.5">
                          {tier.label} −{priceInfo.percent}%
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-serif text-base font-semibold text-[#1F4D3F]">
                          {formatPrice(minPrice)}
                        </div>
                        {maxPrice > minPrice && (
                          <div className="text-[10px] text-[#7A6F5C] mt-0.5">
                            jusqu'à {formatPrice(maxPrice)}
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Livraison */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex items-start gap-1.5 text-xs">
                      <Truck className="w-3.5 h-3.5 text-[#1F4D3F] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-[#2D2A26]">24-48h</div>
                        <div className="text-[10px] text-[#7A6F5C]">Paiement livraison</div>
                      </div>
                    </div>
                  </td>

                  {/* Action */}
                  <td className="px-4 py-3 text-right">
                    {inStock ? (
                      <Button
                        size="sm"
                        data-testid={`table-add-cart-${p.id}`}
                        onClick={(e) => { e.preventDefault(); onAddToCart && onAddToCart(p, 0); }}
                        className="bg-[#1F4D3F] hover:bg-[#173B30] text-white h-8 px-3">
                        <ShoppingCart className="w-3.5 h-3.5 sm:mr-1" />
                        <span className="hidden sm:inline">Ajouter</span>
                      </Button>
                    ) : (
                      <span className="text-xs text-rose-600">Rupture</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {products.length === 0 && (
        <div className="p-10 text-center text-[#7A6F5C]">Aucun produit</div>
      )}
    </div>
  );
};

export default ProductTableView;
